import { Loader2 } from 'lucide-react';
import { EGYPTIAN_GOVERNORATES } from '../scholarshipApplication/constants';
import {
  BACKUP_PHONE_RELATION_OPTIONS,
  FLOOR_OPTIONS,
} from './acceptanceValidation';
import CityField from './CityField';
import SocialShareConsent from './SocialShareConsent';
import type {
  AcceptanceFieldErrors,
  AcceptanceFieldKey,
  AcceptanceFormValues,
  BackupPhoneRelation,
} from './types';

interface AddressFormProps {
  formId: string;
  values: AcceptanceFormValues;
  errors: AcceptanceFieldErrors;
  saving: boolean;
  saveError: string | null;
  onFieldChange: <K extends keyof AcceptanceFormValues>(
    key: K,
    value: AcceptanceFormValues[K]
  ) => void;
  onGovernorateChange: (governorate: string) => void;
  onFloorChange: (floor: string) => void;
  onFieldBlur: (field: AcceptanceFieldKey) => void;
  onSubmit: () => void;
}

const INPUT_BASE_CLASS =
  'min-h-11 w-full rounded-xl border-2 bg-white px-4 py-3 text-base text-[#2C2C2C] transition-colors placeholder:text-[#707070]/60 focus:outline-none';

function fieldBorderClass(hasError: boolean): string {
  return hasError
    ? 'border-[#F46A6A] focus:border-[#F46A6A] focus:ring-4 focus:ring-[#F46A6A]/10'
    : 'border-gray-200 focus:border-[#5B3FD0] focus:ring-4 focus:ring-[#5B3FD0]/10';
}

export default function AddressForm({
  formId,
  values,
  errors,
  saving,
  saveError,
  onFieldChange,
  onGovernorateChange,
  onFloorChange,
  onFieldBlur,
  onSubmit,
}: AddressFormProps) {
  const idFor = (field: string) => `${formId}-${field}`;
  const errorIdFor = (field: string) => `${formId}-${field}-error`;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      noValidate
      className="space-y-5"
    >
      {/* Share your celebration consent */}
      <SocialShareConsent
        formId={formId}
        value={values.social_share_consent}
        error={errors.social_share_consent}
        onChange={(val) => onFieldChange('social_share_consent', val)}
      />

      {/* Address explanation */}
      <div className="rounded-2xl border border-[#E6E1F2] bg-white px-4 py-3.5 text-sm font-medium leading-7 text-[#2C2C2C] shadow-2xs sm:px-5">
        📬 عشان نبعتلك خطاب القبول لحد البيت، اكتب عنوانك بالتفصيل ورقمين موبايل. خد بالك من كل التفاصيل — المندوب هيوصلك من العنوان ده.
      </div>

      {/* Card 1: المستلم */}
      <section className="rounded-3xl border border-[#E6E1F2] bg-white p-5 shadow-xs sm:p-6">
        <h2 className="text-lg font-extrabold text-[#5B3FD0]">المستلم</h2>
        <div className="mt-4">
          <label
            htmlFor={idFor('recipient_name')}
            className="mb-2 block text-sm font-bold text-[#2C2C2C]"
          >
            اسم المستلم بالكامل
            <span className="ms-1 text-[#F46A6A]">*</span>
          </label>
          <input
            id={idFor('recipient_name')}
            name="recipient_name"
            type="text"
            autoComplete="name"
            maxLength={120}
            value={values.recipient_name}
            aria-invalid={Boolean(errors.recipient_name)}
            aria-describedby={
              errors.recipient_name
                ? errorIdFor('recipient_name')
                : `${formId}-recipient-helper`
            }
            onChange={(e) => onFieldChange('recipient_name', e.target.value)}
            onBlur={() => onFieldBlur('recipient_name')}
            className={`${INPUT_BASE_CLASS} ${fieldBorderClass(Boolean(errors.recipient_name))}`}
          />
          {errors.recipient_name ? (
            <p
              id={errorIdFor('recipient_name')}
              role="alert"
              className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
            >
              {errors.recipient_name}
            </p>
          ) : (
            <p
              id={`${formId}-recipient-helper`}
              className="mt-1.5 text-xs text-[#707070]"
            >
              الاسم اللي هيتكتب على الظرف
            </p>
          )}
        </div>
      </section>

      {/* Card 2: العنوان */}
      <section className="rounded-3xl border border-[#E6E1F2] bg-white p-5 shadow-xs sm:p-6">
        <h2 className="text-lg font-extrabold text-[#5B3FD0]">العنوان</h2>
        <div className="mt-4 space-y-4">
          {/* Governorate */}
          <div>
            <label
              htmlFor={idFor('governorate')}
              className="mb-2 block text-sm font-bold text-[#2C2C2C]"
            >
              المحافظة
              <span className="ms-1 text-[#F46A6A]">*</span>
            </label>
            <select
              id={idFor('governorate')}
              name="governorate"
              value={values.governorate}
              aria-invalid={Boolean(errors.governorate)}
              aria-describedby={
                errors.governorate ? errorIdFor('governorate') : undefined
              }
              onChange={(e) => onGovernorateChange(e.target.value)}
              onBlur={() => onFieldBlur('governorate')}
              className={`${INPUT_BASE_CLASS} ${fieldBorderClass(Boolean(errors.governorate))}`}
            >
              <option value="">اختار المحافظة</option>
              {EGYPTIAN_GOVERNORATES.map((gov) => (
                <option key={gov.value} value={gov.value}>
                  {gov.label}
                </option>
              ))}
            </select>
            {errors.governorate && (
              <p
                id={errorIdFor('governorate')}
                role="alert"
                className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
              >
                {errors.governorate}
              </p>
            )}
          </div>

          {/* City */}
          <CityField
            formId={formId}
            governorate={values.governorate}
            cityChoice={values.cityChoice}
            customCity={values.customCity}
            error={errors.city}
            onSelectChange={(val) => onFieldChange('cityChoice', val)}
            onCustomChange={(val) => onFieldChange('customCity', val)}
            onBlur={() => onFieldBlur('city')}
          />

          {/* Area */}
          <div>
            <label
              htmlFor={idFor('area')}
              className="mb-2 block text-sm font-bold text-[#2C2C2C]"
            >
              المنطقة / الحي / القرية
              <span className="ms-1 text-[#F46A6A]">*</span>
            </label>
            <input
              id={idFor('area')}
              name="area"
              type="text"
              maxLength={120}
              value={values.area}
              placeholder="مثال: الحي السابع"
              aria-invalid={Boolean(errors.area)}
              aria-describedby={errors.area ? errorIdFor('area') : undefined}
              onChange={(e) => onFieldChange('area', e.target.value)}
              onBlur={() => onFieldBlur('area')}
              className={`${INPUT_BASE_CLASS} ${fieldBorderClass(Boolean(errors.area))}`}
            />
            {errors.area && (
              <p
                id={errorIdFor('area')}
                role="alert"
                className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
              >
                {errors.area}
              </p>
            )}
          </div>

          {/* Street */}
          <div>
            <label
              htmlFor={idFor('street')}
              className="mb-2 block text-sm font-bold text-[#2C2C2C]"
            >
              اسم الشارع
              <span className="ms-1 text-[#F46A6A]">*</span>
            </label>
            <input
              id={idFor('street')}
              name="street"
              type="text"
              autoComplete="address-line1"
              maxLength={150}
              value={values.street}
              placeholder="مثال: شارع عباس العقاد"
              aria-invalid={Boolean(errors.street)}
              aria-describedby={errors.street ? errorIdFor('street') : undefined}
              onChange={(e) => onFieldChange('street', e.target.value)}
              onBlur={() => onFieldBlur('street')}
              className={`${INPUT_BASE_CLASS} ${fieldBorderClass(Boolean(errors.street))}`}
            />
            {errors.street && (
              <p
                id={errorIdFor('street')}
                role="alert"
                className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
              >
                {errors.street}
              </p>
            )}
          </div>

          {/* Building */}
          <div>
            <label
              htmlFor={idFor('building')}
              className="mb-2 block text-sm font-bold text-[#2C2C2C]"
            >
              رقم العمارة / المنزل
              <span className="ms-1 text-[#F46A6A]">*</span>
            </label>
            <input
              id={idFor('building')}
              name="building"
              type="text"
              inputMode="text"
              maxLength={20}
              value={values.building}
              placeholder="مثال: 12 أو 12أ"
              aria-invalid={Boolean(errors.building)}
              aria-describedby={
                errors.building ? errorIdFor('building') : undefined
              }
              onChange={(e) => onFieldChange('building', e.target.value)}
              onBlur={() => onFieldBlur('building')}
              className={`${INPUT_BASE_CLASS} ${fieldBorderClass(Boolean(errors.building))}`}
            />
            {errors.building && (
              <p
                id={errorIdFor('building')}
                role="alert"
                className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
              >
                {errors.building}
              </p>
            )}
          </div>

          {/* Floor */}
          <div>
            <label
              htmlFor={idFor('floor')}
              className="mb-2 block text-sm font-bold text-[#2C2C2C]"
            >
              الدور
              <span className="ms-1 text-[#F46A6A]">*</span>
            </label>
            <select
              id={idFor('floor')}
              name="floor"
              value={values.floor}
              aria-invalid={Boolean(errors.floor)}
              aria-describedby={errors.floor ? errorIdFor('floor') : undefined}
              onChange={(e) => onFloorChange(e.target.value)}
              onBlur={() => onFieldBlur('floor')}
              className={`${INPUT_BASE_CLASS} ${fieldBorderClass(Boolean(errors.floor))}`}
            >
              <option value="">اختار الدور</option>
              {FLOOR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.floor && (
              <p
                id={errorIdFor('floor')}
                role="alert"
                className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
              >
                {errors.floor}
              </p>
            )}
          </div>

          {/* Apartment (hidden when floor === 'house') */}
          {values.floor !== 'house' && (
            <div>
              <label
                htmlFor={idFor('apartment')}
                className="mb-2 block text-sm font-bold text-[#2C2C2C]"
              >
                رقم الشقة
              </label>
              <input
                id={idFor('apartment')}
                name="apartment"
                type="text"
                maxLength={20}
                value={values.apartment}
                placeholder="مثال: 8 (اختياري)"
                aria-invalid={Boolean(errors.apartment)}
                aria-describedby={
                  errors.apartment ? errorIdFor('apartment') : undefined
                }
                onChange={(e) => onFieldChange('apartment', e.target.value)}
                onBlur={() => onFieldBlur('apartment')}
                className={`${INPUT_BASE_CLASS} ${fieldBorderClass(Boolean(errors.apartment))}`}
              />
              {errors.apartment && (
                <p
                  id={errorIdFor('apartment')}
                  role="alert"
                  className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
                >
                  {errors.apartment}
                </p>
              )}
            </div>
          )}

          {/* Landmark */}
          <div>
            <label
              htmlFor={idFor('landmark')}
              className="mb-2 block text-sm font-bold text-[#2C2C2C]"
            >
              أقرب علامة مميزة
              <span className="ms-1 text-[#F46A6A]">*</span>
            </label>
            <textarea
              id={idFor('landmark')}
              name="landmark"
              rows={2}
              maxLength={200}
              value={values.landmark}
              aria-invalid={Boolean(errors.landmark)}
              aria-describedby={
                errors.landmark
                  ? errorIdFor('landmark')
                  : `${formId}-landmark-helper`
              }
              onChange={(e) => onFieldChange('landmark', e.target.value)}
              onBlur={() => onFieldBlur('landmark')}
              className={`${INPUT_BASE_CLASS} ${fieldBorderClass(Boolean(errors.landmark))}`}
            />
            {errors.landmark ? (
              <p
                id={errorIdFor('landmark')}
                role="alert"
                className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
              >
                {errors.landmark}
              </p>
            ) : (
              <p
                id={`${formId}-landmark-helper`}
                className="mt-1.5 text-xs text-[#707070]"
              >
                مثال: جنب مسجد النور، قدام صيدلية الشفاء
              </p>
            )}
          </div>

          {/* Postal Code */}
          <div>
            <label
              htmlFor={idFor('postal_code')}
              className="mb-2 block text-sm font-bold text-[#2C2C2C]"
            >
              الرقم البريدي (اختياري)
            </label>
            <input
              id={idFor('postal_code')}
              name="postal_code"
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              dir="ltr"
              maxLength={5}
              value={values.postal_code}
              placeholder="11765"
              aria-invalid={Boolean(errors.postal_code)}
              aria-describedby={
                errors.postal_code ? errorIdFor('postal_code') : undefined
              }
              onChange={(e) => onFieldChange('postal_code', e.target.value)}
              onBlur={() => onFieldBlur('postal_code')}
              className={`${INPUT_BASE_CLASS} text-start ${fieldBorderClass(Boolean(errors.postal_code))}`}
            />
            {errors.postal_code && (
              <p
                id={errorIdFor('postal_code')}
                role="alert"
                className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
              >
                {errors.postal_code}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Card 3: أرقام التواصل */}
      <section className="rounded-3xl border border-[#E6E1F2] bg-white p-5 shadow-xs sm:p-6">
        <h2 className="text-lg font-extrabold text-[#5B3FD0]">أرقام التواصل</h2>
        <div className="mt-4 space-y-4">
          {/* Main phone */}
          <div>
            <label
              htmlFor={idFor('phone')}
              className="mb-2 block text-sm font-bold text-[#2C2C2C]"
            >
              رقم موبايلك
              <span className="ms-1 text-[#F46A6A]">*</span>
            </label>
            <input
              id={idFor('phone')}
              name="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              dir="ltr"
              placeholder="01xxxxxxxxx"
              value={values.phone}
              aria-invalid={Boolean(errors.phone)}
              aria-describedby={errors.phone ? errorIdFor('phone') : undefined}
              onChange={(e) => onFieldChange('phone', e.target.value)}
              onBlur={() => onFieldBlur('phone')}
              className={`${INPUT_BASE_CLASS} text-start ${fieldBorderClass(Boolean(errors.phone))}`}
            />
            {errors.phone && (
              <p
                id={errorIdFor('phone')}
                role="alert"
                className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
              >
                {errors.phone}
              </p>
            )}
          </div>

          {/* Backup phone */}
          <div>
            <label
              htmlFor={idFor('backup_phone')}
              className="mb-2 block text-sm font-bold text-[#2C2C2C]"
            >
              رقم موبايل احتياطي
              <span className="ms-1 text-[#F46A6A]">*</span>
            </label>
            <input
              id={idFor('backup_phone')}
              name="backup_phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              dir="ltr"
              placeholder="01xxxxxxxxx"
              value={values.backup_phone}
              aria-invalid={Boolean(errors.backup_phone)}
              aria-describedby={
                errors.backup_phone ? errorIdFor('backup_phone') : undefined
              }
              onChange={(e) => onFieldChange('backup_phone', e.target.value)}
              onBlur={() => onFieldBlur('backup_phone')}
              className={`${INPUT_BASE_CLASS} text-start ${fieldBorderClass(Boolean(errors.backup_phone))}`}
            />
            {errors.backup_phone && (
              <p
                id={errorIdFor('backup_phone')}
                role="alert"
                className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
              >
                {errors.backup_phone}
              </p>
            )}
          </div>

          {/* Backup phone relation */}
          <div>
            <label
              htmlFor={idFor('backup_phone_relation')}
              className="mb-2 block text-sm font-bold text-[#2C2C2C]"
            >
              الرقم الاحتياطي ده لمين؟
              <span className="ms-1 text-[#F46A6A]">*</span>
            </label>
            <select
              id={idFor('backup_phone_relation')}
              name="backup_phone_relation"
              value={values.backup_phone_relation}
              aria-invalid={Boolean(errors.backup_phone_relation)}
              aria-describedby={
                errors.backup_phone_relation
                  ? errorIdFor('backup_phone_relation')
                  : undefined
              }
              onChange={(e) =>
                onFieldChange(
                  'backup_phone_relation',
                  e.target.value as BackupPhoneRelation | ''
                )
              }
              onBlur={() => onFieldBlur('backup_phone_relation')}
              className={`${INPUT_BASE_CLASS} ${fieldBorderClass(Boolean(errors.backup_phone_relation))}`}
            >
              <option value="">اختار صلة القرابة</option>
              {BACKUP_PHONE_RELATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.backup_phone_relation && (
              <p
                id={errorIdFor('backup_phone_relation')}
                role="alert"
                className="mt-1.5 text-sm font-semibold text-[#F46A6A]"
              >
                {errors.backup_phone_relation}
              </p>
            )}
          </div>
        </div>
      </section>

      {saveError && (
        <div
          role="alert"
          className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold leading-7 text-red-800"
        >
          {saveError}
        </div>
      )}

      <button
        type="submit"
        disabled={saving}
        className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#5B3FD0] px-6 py-3.5 text-base font-bold text-white shadow-md transition-colors hover:bg-[#4729b0] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {saving && <Loader2 className="h-5 w-5 animate-spin" />}
        <span>{saving ? 'جاري الحفظ...' : '💾 احفظ بياناتي'}</span>
      </button>
    </form>
  );
}
